import { SuccessAlertContext } from "App";
import { Box, Button, SpeedDial, SxProps, Typography } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { API_URL } from "config";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Notify from "config/notify";
import ERROR_MESSAGE from "constants/message";
import { checkAccessTokenExpired, getUserType } from "util/auth";
import { localEnv } from "features/order/constants";
import { HelpOutline } from "@mui/icons-material";
import SurroundLine from "../components/SurroundLine";
import ProductTable from "../components/ProductTable";
import CsvModals from "../components/CsvModals";
import UpdateConfirmTable from "../components/UpdateConfirmTable";
import DeleteConfirmTable from "../components/DeleteConfirmTable";
import ProductSettingSearchDialog from "../components/ProductSettingSearchDialog";
import ProductCodeSearchDialog from "../components/ProductCodeSearchDialog";
import productSelectApi from "../api/productSelectApi";
import useProductSetting from "../hooks/useProductSetting";
import type {
  ManagedItem,
  ItemSelectResponse,
  ItemSelectParams,
  JoyfulEditItem,
  CSVCountCheckOrder,
  ItemSearch,
  ItemSearchParams,
} from "../types";
import useRowSelect from "../hooks/useRowSelect";
import useProductSearch from "../hooks/useProductSearch";
import downloadJoyfulSampleCsv from "../api/downloadJoyfulSampleApi";
import useJoyfulSearch from "../hooks/useJoyfulSearch";
import { removeEmptyValues } from "../utils/common";
import downloadHelpPdf from "../api/downloadHelpPdf";

type ActionButtonsProps = {
  onClose: () => void;
  items: ManagedItem[];
  disabled: boolean;
  isDeleteModal?: boolean;
  isUpdateModal?: boolean;
  handleRegister: (rows?: ManagedItem[]) => Promise<void>;
  handleUpdate: (rows?: ManagedItem[]) => Promise<void>;
  handleDelete: () => Promise<void>;
};

/**
 *  削除&更新ボータン
 * @param root0 ボータンのprops
 * @returns JSX.Element
 */
function ActionButtons({
  items,
  disabled,
  isDeleteModal = undefined,
  isUpdateModal = undefined,
  onClose,
  handleUpdate,
  handleDelete,
  handleRegister,
}: ActionButtonsProps) {
  return (
    <>
      <Button variant="custom-outlined" onClick={onClose}>
        キャンセル
      </Button>
      <Button
        variant="contained"
        onClick={() => {
          if (isDeleteModal) {
            handleDelete();
          } else if (isUpdateModal) {
            handleUpdate(items);
          } else {
            handleRegister(items);
          }
        }}
        disabled={disabled}
      >
        {isDeleteModal ? "削除" : isUpdateModal ? "更新" : "登録"}
      </Button>
    </>
  );
}

/**
 * ジョイフル商品用設定画面
 * @returns JSX.Element
 */
export default function ProductSetting() {
  const [isOpenStockCsvUpload, setIsOpenStockCsvUpload] = useState(false); // CSVアップロードモーダル開閉
  const handleCsvDialogToggle = () => {
    setIsOpenStockCsvUpload(!isOpenStockCsvUpload);
  };
  const [isOpenCsvConfirm, setIsOpenCsvConfirm] = useState(false); // CSV確認更新モーダル開閉
  const handleCsvConfirmDialogToggle = () => {
    setIsOpenCsvConfirm(!isOpenCsvConfirm);
  };

  const [isOpenUpdate, setIsOpenUpdate] = useState(false); // 更新モーダル開閉
  const handleUpdateDialogToggle = () => {
    setIsOpenUpdate(!isOpenUpdate);
  };

  const originalRows = [
    {
      item_cd: "000001",
      jan_cd: "000000000001",
      class_name: "タイヤ",
      vendor_name: "ブリヂストン",
      brand: "ボテンザ",
      item_name: "S007A",
      size: "185/65R/15",
      store_wholesale_price: "10300",
      errors: [],
    },
  ];

  const newData: CSVCountCheckOrder[] = originalRows.map((r) => ({
    item_cd: r.item_cd,
    jan_cd: r.jan_cd,
    class_name: r.class_name,
    vendor_name: r.vendor_name,
    brand: r.brand,
    item_name: r.item_name,
    size: r.size,
    store_wholesale_price: Number(r.store_wholesale_price),
    errors: r.errors ?? [],
  }));

  const [isOpenThresholdCsvUpload, setIsOpenThresholdCsvUpload] = useState(false);
  const { showAlert } = useContext(SuccessAlertContext);

  const location = useLocation();
  const isAuthorizedUser = useRef(true);

  // ユーザーが適切かチェック
  const checkAuthorized = useCallback(() => {
    console.log("checkAuthorized");

    if (!localEnv) {
      const userType = localStorage.getItem("user_type");
      if (!(userType === "C0009")) {
        isAuthorizedUser.current = false;
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: "ジョイフルユーザーで再度ログインしてください。",
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      } else if (checkAccessTokenExpired()) {
        isAuthorizedUser.current = false;
        // 期限切れなら
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      }
    }
  }, [isAuthorizedUser.current]);

  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const handleDeleteDialogToggle = () => {
    setIsOpenDelete(!isOpenDelete);
  };

  // サンプルCSVダウンロード処理
  const onClickDownloadCsv = async () => {
    await downloadJoyfulSampleCsv();
    // const response = await fetch(`${API_URL}/joyful/item/csv/download`, { method: "get" });
    // if (response.ok) {
    //   const blob = await response.blob();
    //   const url = window.URL.createObjectURL(blob);
    //   const a = document.createElement("a");
    //   a.href = url;
    //   a.download = "sample.csv";
    //   a.click();
    // }
  };

  const handleTableDataChange = (data: ManagedItem[]) => {
    // setChangedData(data);
  };

  const styles: { [key: string]: SxProps } = {
    buttonContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      position: "relative",
      padding: "5px",
    },
    leftButtonContainer: {
      position: "absolute",
      top: 0,
      left: 0,
    },
  };

  // 絞込検索モーダル開閉
  const [isOpenProductSearch, setIsOpenProductSearch] = useState(false);
  const handleSearchDialogToggle = () => {
    setIsOpenProductSearch(!isOpenProductSearch);
  };
  // 商品登録モーダル開閉
  const [isOpenProductCodeSearch, setIsOpenProductCodeSearch] = useState(false);
  const handleSearchCodeDialogToggle = () => {
    setIsOpenProductCodeSearch(!isOpenProductCodeSearch);
  };
  // 閲覧・更新切替
  const [isUpdateDisplay, setIsUpdateDisplay] = useState(true);
  const handleReadUpdateToggle = () => {
    setIsUpdateDisplay(!isUpdateDisplay);
  };

  const navigate = useNavigate();
  const classNameOption = ["タイヤ", "ホイール"] as const;
  const classDict: {[K in typeof classNameOption[number]]: string} = {"タイヤ": "02", "ホイール": "03"}
  const [classCd, setClassCd] = useState("");
  const [className, setClassName] = useState("");
  const [productSrc, setProductSrc] = useState("");
  // const widthOptions = ["165", "175", "185"];
  // const [width, setWidth] = useState("");
  // const oblatenessOptions = ["45", "55", "65"];
  // const [oblateness, setOblateness] = useState("");
  // const tireRimDmtrOptions = ["14", "15", "16"];
  // const [tireRimDmtr, setTireRimDmtr] = useState("");
  // const wheelRimDmtrOptions = ["38", "40", "43"];
  // const [wheelRimDmtr, setWheelRimDmtr] = useState("");
  // const rimWidthOptions = ["19", "20", "22"];
  // const [rimWidth, setRimWidth] = useState("");
  // const holeNmbrOptions = ["4", "5", "6"];
  // const [holeNmbr, setHoleNmbr] = useState("");
  // const PCDOptions = ["100", "114"];
  // const [PCD, setPCD] = useState("");

  const itemTypeOptions = ["タイヤ", "ホイール"] as const;
  const [itemType, setItemType] = useState<"" | "タイヤ" | "ホイール">("");
  // const itemSrcOptions = ["A", "B", "C"];
  const [itemSrc, setItemSrc] = useState("");
  // const brandOptions = ["A", "B", "C"];
  const [brandName, setBrandName] = useState("");
  const [itemName, setItemName] = useState("");
  const [bckgrndClr, setBckgrndClr] = useState("");
  const [itemCds, setItemCds] = useState<string[]>([]);
  const [isUnsetUnitPrice, setIsUnsetUnitPrice] = useState(false);

  // URLから企業コードを取得する
  const [searchParams] = useSearchParams();
  const companyCd = searchParams.get("company_cd") || "";
  const companyName = searchParams.get("company_name") || "";

  const [loading, setLoading] = useState(false); // メイン商品情報ローディング
  const [isMoreLoading, setIsMoreLoading] = useState(false); // 追加読み込みローディング
  const [editedPrices, setEditedPrices] = useState<Record<string, number | null>>({}); // 空の場合はnull
  const [selectedRows, setSelectedRows] = useState<ItemSearch[]>([]);

  // 追加ローディング処理
  const [fireMoreLoadEvent, setFireMoreLoadEvent] = useState(false);
  const isFastLoading = useRef(false); // 二重読み込み防止
  const [isScrollTop, setIsScrollTop] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // メインテーブル状態管理
  const [items, setItems] = useState<ManagedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const limit = 10;

  const [reloadKey, setReloadKey] = useState(0);

  // チェックボックスの選択処理
  const {
    selectedRowIds: selectedIds,
    isSelected,
    isSelectedAll,
    isIndeterminate,
    toggleSelected,
    toggleSelectedAll,
  } = useRowSelect(items.map((item) => parseInt(item.item_cd, 10)));

  // 商品絞込検索用hooks
  const { productItems, productLoading, init, setProductItems, setProductLoading, fetchData, setInit } =
    useProductSearch(companyCd);

  // 検索条件のstate
  const [
    {
      isCustomerOrder,
      shopName,
      orderStatus,
      orderReceiptStatus,
      storeOrderer,
      approver,
      orderStartDate,
      orderEndDate,
      companyCdField,
      centerCd,
      orderNumber,
      productCd,
      productName,
      brand,
      janCd,
      productNumber,
      tyreWidthCd,
      tyreRatioCd,
      tyreRimDiamCd,
      wheelWidthCd,
      wheelPcdCd,
      wheelHoleCd,
      wheelRimDiamCd,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouse,
      supplier,
    },
    {
      setIsCustomerOrder,
      setShopName,
      setOrderStatus,
      setOrderReceiptStatus,
      setStoreOrderer,
      setApprover,
      setOrderStartDate,
      setOrderEndDate,
      setCompanyCdField,
      setCenterCd,
      setOrderNumber,
      setProductCd,
      setProductName,
      setBrand,
      setJanCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setTyreWidthCd,
      setTyreRatioCd,
      setTyreRimDiamCd,
      setWheelWidthCd,
      setWheelPcdCd,
      setWheelHoleCd,
      setWheelRimDiamCd,
      setClassification,
      setStorehouse,
      setSupplier,
    },
    { classificationOptions, tyreOptions, wheelOptions, brandOptions, supplierOptions },
    { isLoading: isOptionsLoading },
  ] = useJoyfulSearch(isAuthorizedUser.current);

  const resetRegisterValues = () => {
    console.log('商品登録モーダルリセット')
    setItemType("");
    setItemSrc("")
    setBrandName("");
    setTyreWidth(undefined);
    setTyreRatio(undefined);
    setTyreRimDiam(undefined);
    setWheelRimDiam(undefined);
    setWheelWidth(undefined);
    setWheelHole(undefined);
    setWheelPcd(undefined);
    setItemName("");
    setProductItems([]);
    setSelectedRows([]);
  }

  // 更新対象をitemsで返す
  const getChangedItems = () => items.filter(it => Object.entries(editedPrices).map(([item_cd, store_wholesale_price]) => ({
    item_cd,
    store_wholesale_price,
  })).some(editedPriceItem =>
    // 同じ商品コードだが価格が違う商品があれば更新対象
    it.item_cd === editedPriceItem.item_cd &&
    it.store_wholesale_price !== editedPriceItem.store_wholesale_price
  ))

  // 更新対象をeditedPricesを固定値key、valueにして返す
  const getEditedPrices = () => Object.entries(editedPrices).map(([item_cd, store_wholesale_price]) => ({
    item_cd,
    store_wholesale_price,
  })).filter(editedPriceItem => items.some(it =>
    // 同じ商品コードだが価格が違う商品があれば更新対象
    it.item_cd === editedPriceItem.item_cd &&
    it.store_wholesale_price !== editedPriceItem.store_wholesale_price
  ));

  // 商品情報を再読み込み
  const fetchItems = async () => {
    if (!companyCd) return;
    setLoading(true);

    try {
      const params: ItemSelectParams = {
        company_cd: companyCd,
        limit,
        offset: 0,
        item_cd: productCd,
        jan_cd: janCd,
        class_cd: classDict[className as typeof classNameOption[number]] || undefined,
        vendor_cd: supplierOptions.find(so => so.supplierName === productSrc)?.supplierCd || undefined,
        brand: brand || undefined,
        item_name: productName || undefined,
        cost_unset: Number(isUnsetUnitPrice),
        tire_width: className === "タイヤ" ? tyreWidth : undefined,
        tire_ratio: className === "タイヤ" ? tyreRatio : undefined,
        tire_rim_size: className === "タイヤ" ? tyreRimDiam : undefined,
        wheel_width: className === "ホイール" ? wheelWidth : undefined,
        wheel_rim_size: className === "ホイール" ? wheelRimDiam : undefined,
        wheel_hole: className === "ホイール" ? wheelHole : undefined,
        wheel_pcd: className === "ホイール" ? wheelPcd : undefined,
      };
      // const params: ItemSelectParams = {
      //   company_cd: companyCd,
      //   item_cd: productCd,
      //   jan_cd: janCd,
      //   class_cd: classCd,
      //   limit,
      //   offset: 0,
      // };
      const res = await productSelectApi(removeEmptyValues(params));
      setTotal(res.total);
      setItems(res.managed_items || []);
      setOffset(res.managed_items.length);
      setEditedPrices(res.managed_items.reduce((acc, {item_cd, store_wholesale_price}) => {
        acc[item_cd] = store_wholesale_price;
        return acc
      }, {} as Record<string, number>));
    } catch (err) {
      console.error(err);
      // setItems([]);
    } finally {
      setLoading(false);
      isFastLoading.current = false;
    }
  }

  const fetchMoreItems = async () => {
    if (!init || items.length >= total || !companyCd) {
      isFastLoading.current = false;
      return;
    }
    setIsMoreLoading(true);

    try {
      const params: ItemSelectParams = {
        company_cd: companyCd,
        limit,
        offset,
        item_cd: productCd,
        jan_cd: janCd,
        class_cd: classDict[className as typeof classNameOption[number]] || undefined,
        vendor_cd: supplierOptions.find(so => so.supplierName === productSrc)?.supplierCd || undefined,
        brand: brand || undefined,
        item_name: productName || undefined,
        cost_unset: Number(isUnsetUnitPrice),
        tire_width: className === "タイヤ" ? tyreWidth : undefined,
        tire_ratio: className === "タイヤ" ? tyreRatio : undefined,
        tire_rim_size: className === "タイヤ" ? tyreRimDiam : undefined,
        wheel_width: className === "ホイール" ? wheelWidth : undefined,
        wheel_rim_size: className === "ホイール" ? wheelRimDiam : undefined,
        wheel_hole: className === "ホイール" ? wheelHole : undefined,
        wheel_pcd: className === "ホイール" ? wheelPcd : undefined,
      };
      const res = await productSelectApi(params);
      setTotal(res.total);
      setOffset(prev => prev + res.managed_items.length);
      setItems(prev => [...prev, ...res.managed_items]);
      setEditedPrices(prev => ({...prev, ...res.managed_items.reduce((acc, {item_cd, store_wholesale_price}) => {
        acc[item_cd] = store_wholesale_price;
        return acc
      }, {} as Record<string, number>)}));
    } catch (err) {
      console.error(err);
      // setItems([]);
    } finally {
      setIsMoreLoading(false);
      isFastLoading.current = false;
    }
  };

  useEffect(() => {
    setTimeout(() => {
      checkAuthorized()
      if (isAuthorizedUser.current && !companyCd) {
        navigate("/joyful");
      }
    }, 0);
  }, [location.pathname, companyCd]);

  useEffect(() => {
    if (isAuthorizedUser.current) {
      fetchItems();
    }
  }, [reloadKey, companyCd, isAuthorizedUser.current]);

  // 再読み込みハンドラ
  const getStockListUpdate = () => {
    setReloadKey((k) => k + 1);
  };

  // 不使用
  const handleSearchCd = async () => {
    const params: ItemSearchParams = {
      company_cd: companyCd,
      limit,
      class_cd: classDict[className as typeof classNameOption[number]] || undefined,
      vendor_cd: supplierOptions.find(so => itemSrc === so.supplierName)?.supplierCd || undefined,
      brand: brandName || undefined,
      item_name: itemName.trim() || undefined,
    };
    fetchData(params);
  };

  const handleSearch = async () => {
    fetchItems();
  };

  // 商品設定用hooks
  const { isRegistering, isUpdating, isDeleting, registerProduct, updateProduct, deleteProduct } =
    useProductSetting(companyCd);

  // 設定登録ボータンハンドル
  const handleRegister = async (rows?: ItemSearch[]) => {
    console.log('register');
    const target: ItemSearch[] = rows?.length ? rows : ([] as ManagedItem[]);

    if (!target.length) {
      showAlert("登録データがありません", "error");
      return;
    }
    await registerProduct({
      company_cd: companyCd,
      managed_items: target.map(t => ({...t, ...({store_wholesale_price: null})})),
    });
    // データを再読み込み
    // await fetchData({ company_cd: companyCd, limit});
    await fetchItems();
    setIsOpenProductCodeSearch(false);
    resetRegisterValues();
  };

  // 商品設定更新ボータンハンドル
  const handleUpdate = async () => {
    const changeItems = getEditedPrices();
    if (changeItems.length === 0) {
      showAlert("変更データがありません", "error");
      return;
    }
    // console.log(items);
    await updateProduct({
      company_cd: companyCd,
      managed_items: changeItems,
    });

    // データを再読み込み
    // await fetchData({ company_cd: companyCd, limit: 20, offset: 0 });
    await fetchItems();
    setIsOpenUpdate(false);
  };

  // 商品設定削除ボータンハンドル
  const handleDelete = async () => {
    console.log('delete');
    const filteredItemCds = items.filter((it) => selectedIds.includes(parseInt(it.item_cd, 10))).map((it) => it.item_cd);
    if (filteredItemCds.length === 0) {
      showAlert("削除する項目を選択してください", "error");
      return;
    }
    await deleteProduct(filteredItemCds);

    setItemCds([]);
    // データを再読み込み
    // await fetchData();
    await fetchItems();
    setIsOpenDelete(false);
  };

  const resetForm = () => {
    // setClassName("");
    // setTimeout(() => {
    //   setTyreWidth(undefined);
    //   setTyreRatio(undefined);
    //   setTyreRimDiam(undefined);
    //   setWheelWidth(undefined);
    //   setWheelPcd(undefined);
    //   setWheelHole(undefined);
    //   setWheelRimDiam(undefined);
    // }, 200);
  };

  const handleSearchCancel = () => {
    resetForm();
    handleSearchDialogToggle();
  };

  const handleUploadCancel = () => {
    resetRegisterValues();
    handleSearchCodeDialogToggle();
  };

  // 仕切単価変更処理
  const handleWholesalePriceChange = (itemCd: string, newPrice: number | null) => {
    // let newValue = Number(newPrice);
    // if (Number.isNaN(newValue)) {
    //   return;
    // }
    console.log(newPrice);
    setEditedPrices((prev) => ({
      ...prev,
      [itemCd]: Number.isNaN(newPrice) ? null : newPrice,
    }));
    // setItems((prev) =>
    //   prev.map((item) =>
    //     item.item_cd === itemCd ? { ...item, store_wholesale_price: newPrice } : item,
    //   ),
    // );
  };

  const onApplyClick = () => {
    setIsOpenProductCodeSearch(false);
  };

  // スクロール処理
  const handleScroll = async () => {
    setIsScrollTop(window.pageYOffset > 0);

    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current) {
      return;
    }
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      console.log("scroll bottom");
      isFastLoading.current = true;
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      await fetchMoreItems();
    })();
  }, [fireMoreLoadEvent]);

  useEffect(() => {
    (async () => {
      setInit(true);
    })();
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Box width="90%" alignSelf="center" marginLeft="5%" minHeight={330} position="relative" pt={4}>
      <Box position="absolute" top={32} left={0} width="100%" textAlign="center">
        <Typography fontSize={22}>{companyName}&emsp;商品一覧</Typography>
      </Box>
      <Box width="100%" height={32} textAlign="right" mt={-4}>
        <Button
          variant="custom-outlined"
          onClick={async (e) => {
            // apiからpdfのURLを取得
            const url = await downloadHelpPdf(`${getUserType()}/resources/joyful/manual_joyful.pdf`);
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          startIcon={<HelpOutline color="secondary" />}
          sx={{ height: 24, padding: 1, marginTop: 0.5, marginRight: 1 }}
        >
          ヘルプ
        </Button>
      </Box>

      <Box sx={styles.buttonContainer}>
        <Box sx={styles.leftButtonContainer}>
          <Box mx={1}>
            <Button
              variant="custom-outlined"
              startIcon={<ArrowBackIosNewIcon />}
              onClick={() => navigate(-1)}
            >
              <Box>戻る</Box>
            </Button>
          </Box>
        </Box>

        <Box mx={1}>
          <Button variant="contained" onClick={handleSearchDialogToggle}>
            <Box>絞込検索</Box>
          </Button>
        </Box>

        <Box mx={1}>
          <Button variant="contained" onClick={handleReadUpdateToggle}>
            {isUpdateDisplay ? <Box>閲覧モード</Box> : <Box>更新モード</Box>}
          </Button>
        </Box>

        <Box mx={1}>
          <Button
            variant="contained"
            onClick={handleUpdateDialogToggle}
            disabled={items.every((it) => {
              const isPriceChanged = it.item_cd in editedPrices && editedPrices[it.item_cd] === it.store_wholesale_price;
              if (isPriceChanged) {
                return true;
              }
              return false;
            })}
          >
            設定更新
          </Button>
        </Box>

        <Box mx={1}>
          <Button variant="contained" onClick={handleDeleteDialogToggle} disabled={selectedIds.length === 0}>
            設定削除
          </Button>
        </Box>
      </Box>
      <Box sx={styles.buttonContainer}>
        <SurroundLine title="単品登録" sx={{ padding: 1, marginTop: 1, marginX: 1 }}>
          <Box mx={1}>
            <Button variant="contained" onClick={() => handleSearchCodeDialogToggle()}>
              <Box>商品登録</Box>
            </Button>
          </Box>
        </SurroundLine>
        <SurroundLine
          title="一括登録"
          sx={{ padding: 1, marginTop: 1, marginX: 1, display: "flex" }}
        >
          <Box mx={1}>
            <Button variant="contained" onClick={onClickDownloadCsv} fullWidth>
              <Box>サンプルCSV</Box>
            </Button>
          </Box>
          <Box mx={1}>
            <Button variant="contained" fullWidth onClick={handleCsvDialogToggle}>
              <Box>CSVアップロード</Box>
            </Button>
          </Box>
        </SurroundLine>
      </Box>

      {/* メインテーブル */}
      <ProductTable
        total={total}
        items={items}
        selectedIds={selectedIds}
        toggleSelected={toggleSelected}
        onDataChange={handleTableDataChange}
        isSelected={isSelected}
        toggleSelectedAll={toggleSelectedAll}
        isSelectedAll={isSelectedAll}
        isIndeterminate={isIndeterminate}
        editedPrices={editedPrices}
        handleWholesalePriceChange={handleWholesalePriceChange}
        isLoading={loading}
        setIsLoading={setProductLoading}
        isMoreLoading={isMoreLoading}
        setIsMoreLoading={setIsMoreLoading}
        isUpdateDisplay={isUpdateDisplay}
        companyCd={companyCd}
      />
      {/* CSV更新ダウンロードアップロード-モーダル */}
      <CsvModals
        isOpenStockCsvUpload={isOpenStockCsvUpload}
        setIsOpenStockCsvUpload={setIsOpenStockCsvUpload}
        isOpenCsvConfirm={isOpenCsvConfirm}
        setIsOpenCsvConfirm={setIsOpenCsvConfirm}
        refresh={getStockListUpdate}
        isOpenThresholdCsvUpload={isOpenThresholdCsvUpload}
        setIsOpenThresholdCsvUpload={setIsOpenThresholdCsvUpload}
        companyCd={companyCd}
      />
      <UpdateConfirmTable
        open={isOpenUpdate}
        onClose={() => setIsOpenUpdate(false)}
        productList={getChangedItems()}
        editedPrices={getEditedPrices()}
        stockList={newData}
        isUpdating={isUpdating}
        title="商品設定更新確認"
        content="以下の商品設定を更新します。よろしいですか？"
        errorTitle="以下の更新データにエラーがあります。ご確認ください。"
        ActionButtons={
          <ActionButtons
            onClose={handleUpdateDialogToggle}
            items={getChangedItems()}
            handleRegister={handleRegister}
            handleUpdate={handleUpdate}
            handleDelete={handleDelete}
            disabled={isUpdating || getEditedPrices().length === 0}
            isDeleteModal={false}
            isUpdateModal
          />
        }
      />
      <DeleteConfirmTable
        open={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
        }}
        productList={items.filter((i) => selectedIds.includes(parseInt(i.item_cd, 10)))}
        stockList={newData}
        isDeleting={isDeleting}
        title="商品設定削除確認"
        content="以下の商品設定を削除します。よろしいですか？"
        errorTitle="以下の更新データにエラーがあります。ご確認ください。"
        setItemCds={setItemCds}
        ActionButtons={
          <ActionButtons
            items={items}
            onClose={handleDeleteDialogToggle}
            handleRegister={handleRegister}
            handleUpdate={handleUpdate}
            handleDelete={handleDelete}
            disabled={isDeleting || items.length === 0}
            // onCsvUpload={handleDelete}
            isDeleteModal
          />
        }
      />

      <ProductSettingSearchDialog
        open={isOpenProductSearch}
        onClose={handleSearchCancel}
        isLoading={loading}
        onSearch={handleSearch}
        productCd={productCd}
        janCd={janCd}
        classNameOption={classNameOption}
        className={className}
        productSrc={productSrc}
        brand={brand}
        productName={productName}
        widthOptions={tyreOptions.tyreWidthList}
        width={tyreWidth}
        oblatenessOptions={tyreOptions.tyreRatioList}
        oblateness={tyreRatio}
        tireRimDmtrOptions={tyreOptions.tyreRimDiamList}
        tireRimDmtr={tyreRimDiam}
        wheelRimDmtrOptions={wheelOptions.wheelRimDiamList}
        wheelRimDmtr={wheelRimDiam}
        rimWidthOptions={wheelOptions.wheelWidthList}
        rimWidth={wheelWidth}
        holeNmbrOptions={wheelOptions.wheelHoleList}
        holeNmbr={wheelHole}
        PCDOptions={wheelOptions.wheelPcdList}
        PCD={wheelPcd}
        isUnsetUnitPrice={isUnsetUnitPrice}
        setProductCd={setProductCd}
        setJanCd={setJanCd}
        setClassName={setClassName}
        setProductSrc={setProductSrc}
        setBrand={setBrand}
        setProductName={setProductName}
        setWidth={setTyreWidth}
        setOblateness={setTyreRatio}
        setTireRimDmtr={setTyreRimDiam}
        setWheelRimDmtr={setWheelRimDiam}
        setRimWidth={setWheelWidth}
        setHoleNmbr={setWheelHole}
        setPCD={setWheelPcd}
        setIsUnsetUnitPrice={setIsUnsetUnitPrice}
        supplierOptions={supplierOptions}
      />
      <ProductCodeSearchDialog
        items={items}
        selectedRows={selectedRows}
        open={isOpenProductCodeSearch}
        isLoading={isRegistering}
        productLoading={productLoading}
        companyCd={companyCd}
        classCd={classCd}
        className={className}
        productTypeOptions={itemTypeOptions}
        productType={itemType}
        productSrcOptions={supplierOptions}
        productSrc={itemSrc}
        brandOptions={brandOptions}
        brand={brandName}
        widthOptions={tyreOptions.tyreWidthList}
        width={tyreWidthCd}
        oblatenessOptions={tyreOptions.tyreRatioList}
        oblateness={tyreRatioCd}
        tireRimDmtrOptions={tyreOptions.tyreRimDiamList}
        tireRimDmtr={tyreRimDiamCd}
        wheelRimDmtrOptions={wheelOptions.wheelRimDiamList}
        wheelRimDmtr={wheelRimDiamCd}
        rimWidthOptions={wheelOptions.wheelWidthList}
        rimWidth={wheelWidthCd}
        holeNmbrOptions={wheelOptions.wheelHoleList}
        holeNmbr={wheelHoleCd}
        PCDOptions={wheelOptions.wheelPcdList}
        PCD={wheelPcdCd}
        itemName={itemName}
        bckgrndClr={bckgrndClr}
        productItems={productItems}
        setSelectedRows={setSelectedRows}
        setProductItems={setProductItems}
        onClose={handleUploadCancel}
        onSearch={handleSearchCd}
        search={fetchData}
        onApply={handleRegister}
        setProductType={setItemType}
        setProductSrc={setItemSrc}
        setBrand={setBrandName}
        setWidth={setTyreWidthCd}
        setOblateness={setTyreRatioCd}
        setTireRimDmtr={setTyreRimDiamCd}
        setWheelRimDmtr={setWheelRimDiamCd}
        setRimWidth={setWheelWidthCd}
        setHoleNmbr={setWheelHoleCd}
        setPCD={setWheelPcdCd}
        setItemName={setItemName}
        ActionButtons={
          <ActionButtons
            items={items}
            onClose={handleUploadCancel}
            handleRegister={() => handleRegister(selectedRows)}
            handleUpdate={handleUpdate}
            handleDelete={handleDelete}
            disabled={productLoading}
            isDeleteModal={false}
          />
        }
      />
      {isScrollTop && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <SpeedDial
            ariaLabel="Back to top"
            icon={<KeyboardArrowUpIcon />}
            onClick={scrollToTop}
            open // 常に展開済みにしてアイコンだけ表示
            direction="up" // 展開方向は上だがActionは不要
            FabProps={{ size: "small" }}
          />
        </Box>
      )}
    </Box>
  );
}
